import type { CategorySet, InventoryItem } from 'types/shop-bff/types';

/**
 * Common Attribute names associated with inventory items, and inventory item attributes
 */
export enum AttributeName {
  Size = 'size',
  Color = 'color',
  BodyColor = 'bodyColor',
  PrintLocation = 'printLocation',
  Kind = 'kind',
  // typically used to render user-friendly labels for things
  Label = 'defaultText',

  // indicates how elements should be ordered, in relation to other objects
  Order = 'displayOrder',

  // used for color pickers to render color swatch previews
  HexColor = 'hexColor',

  // used for color swatch pickers to render texture previews for color swatches
  // this property is typically found in inventory item option attributes.
  ImageUrl = 'imageUrl',

  // used for color pickers to render color swatch previews.
  // used in categorySet.ConfigurationSet option Attributes.
  // This value is used in a similar way to imageUrl, except
  // it is called media in the configurationSet.
  Media = 'media',

  // used in the categorySet.configurationSet to annotate an attribute as being a configurable
  // field for an inventory item
  Configuration = 'configuration',

  // Greeting Cards size Attribute is misnamed cardSize creating an edge case this type accounts for
  CardSize = 'cardSize',

  /**
   * Experiences field in categorySet payload is used to determine, which inputs
   * in the product configurator form need explicit user input.
   * experiences: [
   *   { name: 'similar_products', value: false },
   *   { name: 'preview_overlay', value: 'color_picker' },
   *   { name: 'add_to_cart_guard', value: 'size' },
   *   { name: 'configuration', value: 'apparel' },
   * ],
   *
   * We need to look for this constant in said property to look for inputs.
   */
  ATCGuardExperience = 'add_to_cart_guard',
  Model = 'model',
  CaseType = 'case_type',
  FrameColor = 'frameColor',
  FrameStyle = 'frameStyle',
  MatteColor = 'matteColor',
  PaperType = 'paperType',
  HandColor = 'handColor',
  Finish = 'finish',
}

export const PRIMARY_ATTRIBUTE_NAMES = Object.freeze([
  'kind',
  'model',
  'product_type_id',
]);

type StringAttributes = Exclude<
  AttributeName,
  AttributeName.Order | AttributeName.Configuration
>;

type NumericAttributes = AttributeName.Order;

/**
 * A type for an object containing a property called Attributes, containing attributes
 * useful for accepting inventory Items, or other attributes.
 */
export type AttributesContainer = Pick<InventoryItem, 'attributes'>;

/**
 * An individual Attribute, for ease of writing functions accepting attributes
 */
export type InventoryItemAttribute = AttributesContainer['attributes'][number];

/**
 * a simplified representation of a category set attribute
 * without the nested "attributes" property, for functions
 * only concerned with key-values
 */
export type SimpleAttribute = Pick<InventoryItemAttribute, 'name' | 'value'>;

export type PartialCategorySet = {
  configurationSet: CategorySet['configurationSet'];
  defaultItem: Pick<CategorySet['defaultItem'], 'attributes'>;
  items?: CategorySet['items'];
};

/**
 * Represents a complete Product Configuration option, with all necessary metadata to render
 * a field, with all available options for a user to select from.
 *
 * The options here here is the raw data from the category set. It is the responsibility of the
 * consumers of this object to analyze the contents, and determine how it will be rendered
 */
export type ConfiguratorField = {
  name: string;
  defaultText: string | undefined;
  displayOrder: number | undefined;
  required?: boolean;
  options: Array<InventoryItemAttribute>;
};

/**
 * Represents a state for a product configurator, as an array of key value-pairs
 * e.g.
 *
 * [
 *  {name: "size", value: "xl" },
 *  {name: "color", value: "green" }
 * ]
 */
export type ConfiguratorValues = Array<SimpleAttribute>;

/**
 * represents the state of the product configurator's internal form component,
 * represented as a map object of key-value pairs
 * e.g.
 *
 * {
 *  "size": "xl",
 *  "color": "green"
 * }
 */
export type FormikValues = Record<string, any>;

/**
 * Represents a flattened inventory option, with attributes and values.
 * e.g. {
 *   value:string,
 *   key:string,
 *   ...
 * }
 *
 */
export type FlatInventoryItemConfiguratorOption = Partial<
  Record<StringAttributes, string> & Record<NumericAttributes, number>
> & { value: string };

export type DSFormChangeType = {
  values: FormikValues;
  initialValues: FormikValues;
};

export type DSFormOnChangeCallback = (
  from: DSFormChangeType,
  to: DSFormChangeType,
  excludeFromAnalytics?: boolean
) => void;
